import React, { Fragment } from 'react'
import {
  List,
  Datagrid,
  TextField,
  Filter,
  ReferenceInput,
  SelectInput,
  TextInput,
  ReferenceArrayInput,
  SelectArrayInput,
  ShowButton,
  EditButton,
  downloadCSV,
  FunctionField,
  Pagination,
  useDataProvider
} from 'react-admin'
import { capitalCase } from 'change-case'
import jsonExport from 'jsonexport/dist'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import HealthIndicator from '../components/HealthIndicator'
import TimeIndicator from '../components/TimeIndicator'
import ScannerIcon from '@material-ui/icons/SettingsRemote'
import IconButton from '@material-ui/core/IconButton'
import TagIcon from './components/TagIcon'

const useStyles = makeStyles(theme => ({
  textInput: { width: 400, marginRight: 10 },
  selectInput: { width: 200 },
  field: {
    display: 'flex'
  },
  actions: {
    display: 'flex'
  },
  indicator: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
}))

const exporter = modalities => {
  const modalitiesForExport = modalities.map(modality => {
    const {
      dashboardIframeUrl,
      logstreamIframeUrl,
      relationshipId,
      metrics,
      relationship,
      ...modalityForExport
    } = modality
    return modalityForExport
  })
  jsonExport(
    modalitiesForExport,
    {
      headers: [
        'id',
        'serialNumber',
        'siteIdNumber',
        'manufacturer',
        'modality',
        'model',
        'siteName',
        'heLevel',
        'heLevelTime',
        'siteAddress',
        'latitude',
        'longitude',
        'softwareVersion',
        'createdDate'
      ]
    },
    (err, csv) => {
      downloadCSV(csv, 'modalities')
    }
  )
}

const ModalityFilter = props => {
  const classes = useStyles()
  const dataProvider = useDataProvider()
  const [tagTypes, setTagTypes] = React.useState([])

  React.useEffect(() => {
    const fetchTagTypes = async () => {
      try {
        const { data } = await dataProvider.getList('tag-types', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
        })
        setTagTypes(data || [])
      } catch (err) {
        console.error('Error fetching tag types:', err)
      }
    }

    fetchTagTypes()
  }, [dataProvider])

  // Key prop forces Filter to re-render when tagTypes change, ensuring React-Admin detects new filters
  return (
    <Filter {...props} key={`filter-${tagTypes.length}`}>
      <TextInput
        label='Search'
        source='q'
        alwaysOn
        className={classes.textInput}
      />
      <ReferenceInput
        label='Device Type'
        source='type'
        reference='modalities/types'
        allowEmpty
      >
        <SelectInput optionText='name' />
      </ReferenceInput>
      <SelectInput
        label='Filter Active/Inactive'
        source='active'
        alwaysOn
        className={classes.selectInput}
        choices={[
          { id: 1, name: 'Only Active' },
          { id: 0, name: 'Only Inactive' },
          { id: 2, name: 'Show All' }
        ]}
      />
      {tagTypes.map((tagType) => (
        <ReferenceArrayInput
          key={tagType.id}
          source={`tag_${tagType.name}`}
          reference="tags"
          label={`Tag: ${tagType.name}`}
          filter={{ tagTypeId: tagType.id }}
          alwaysOn={false}
          style={{ minWidth: '150px' }}
        >
          <SelectArrayInput optionText="name" />
        </ReferenceArrayInput>
      ))}
    </Filter>
  )
}

const ModalityPanel = ({ id, record, resource }) => {
  const FIELDS = [
    'assetNumber',
    'bedName',
    'unitName',
    'inactiveSoftwareVersion',
    'activeSoftwareVersion',
    'systemModel',
    'macAddress',
    'networkMacAddress',
    'ixNetworkIpAddress',
    'mcNetworkIpAddress',
    'pdmType',
    'pdmMacAddress',
    'pdmSerialNumber',
    'pdmSoftwareVersion'
  ]

  const renderField = key =>
    record[key] && (
      <Box key={'field-' + key} mx={2} my={1} width={180}>
        <Typography variant='body2' color='textSecondary'>
          {capitalCase(key)}
        </Typography>{' '}
        <Typography variant='body2'>{record[key]}</Typography>
      </Box>
    )

  return (
    <Box
      my={0}
      mx={2}
      display='flex'
      alignItems='center'
      justifyContent='flex-start'
      flexWrap='wrap'
      flexDirection='row'
    >
      {record.siteAddress && (
        <Box mx={2} my={2} width={180}>
          <Typography variant='body2' color='textSecondary'>
            Location
          </Typography>{' '}
          <Typography variant='body2'>{record.siteAddress}</Typography>
        </Box>
      )}

      {record.credentials && record.manufacturer === 'Siemens' && (
        <Fragment>
          {record.credentials.username && (
            <Box mx={2} my={1} width={180}>
              <Typography variant='body2' color='textSecondary'>
                Login
              </Typography>{' '}
              <Typography variant='body2'>
                {record.credentials.username}
              </Typography>
            </Box>
          )}
          {record.credentials.password && (
            <Box mx={2} my={1} width={180}>
              <Typography variant='body2' color='textSecondary'>
                Key
              </Typography>{' '}
              <Typography variant='body2'>
                {record.credentials.password}
              </Typography>
            </Box>
          )}
          {record.credentials.expiration && (
            <Box mx={2} my={1} width={180}>
              <Typography variant='body2' color='textSecondary'>
                Expiration
              </Typography>{' '}
              <Typography variant='body2'>
                {record.credentials.expiration}
              </Typography>
            </Box>
          )}
          {record.credentials.flexId && (
            <Box mx={2} my={1} width={180}>
              <Typography variant='body2' color='textSecondary'>
                Flex ID
              </Typography>{' '}
              <Typography variant='body2'>
                {record.credentials.flexId}
              </Typography>
            </Box>
          )}
        </Fragment>
      )}

      {FIELDS.map(renderField)}
    </Box>
  )
}

const RemoteScanButton = ({ record }) => {
  if (!record?.pikvmUrl) return null

  const openRemoteScan = () => {
    const url = record.pikvmUrl.includes('/kvm') ? record.pikvmUrl : `${record.pikvmUrl}/kvm`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <IconButton color='primary' onClick={openRemoteScan} title='Open Remote Scan'>
      <ScannerIcon />
    </IconButton>
  )
}

const Actions = (record) => {
  const classes = useStyles()
  return (
    <div className={classes.actions}>
      <ShowButton record={record} label='' basePath='/modalities' />
      <EditButton record={record} label='' basePath='/modalities' />
      {record && <RemoteScanButton record={record} />}
    </div>
  )
}

const ModalityList = props => {
  return (
    <List
      filters={<ModalityFilter />}
      exporter={exporter}
      bulkActionButtons={false}
      title='Devices'
      sort={{ field: 'healthStatusLevel', order: 'DESC' }}
      perPage={50}
      pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
      {...props}
    >
      <Datagrid size='small' rowClick='show' expand={<ModalityPanel />}>
        <FunctionField
          label='Status'
          sortBy='healthStatusLevel'
          render={props => <HealthIndicator {...props} />}
        />
        <FunctionField
          label='Time'
          sortBy='timeStatusLevel'
          render={props => <TimeIndicator {...props} />}
        />
        <TextField source='id' />
        <TextField source='siteName' />
        <TextField source='serialNumber' />
        <TextField source='manufacturer' />
        <TextField source='modality' label='Device' />
        <TextField source='model' />
        <FunctionField
          label='Metrics'
          render={props => (
            <div>
              {props.timeStatusLevel !== 2 ? (
                props.metrics
              ) : (
                <span style={{ opacity: 0.5, fontStyle: 'italic' }}>
                  {`Telemetry data is >48 hrs old on this device`}
                </span>
              )}
            </div>
          )}
        />
        <FunctionField
          label='Tags'
          render={record => <TagIcon record={record} />}
        />
        <FunctionField
          label='Actions'
          render={props => <Actions {...props} />}
        />
      </Datagrid>
    </List>
  )
}

export default ModalityList
